import { AuditData, MapData, PageType, PageExistence } from '../types';

/**
 * Service for fetching and managing audit data
 */
export class DataService {
  private cache = new Map<string, AuditData>();
  private mapDataCache: MapData | null = null;

  /**
   * Fetch audit data for a specific page type
   * @param auditName - Name of the audit
   * @param pageType - Type of page
   * @returns Audit data or null if not found
   */
  async fetchAuditData(auditName: string, pageType: PageType): Promise<AuditData | null> {
    const cacheKey = `${auditName}:${pageType}`;

    // Check cache first
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    try {
      const cacheBuster = `?v=${Date.now()}`;
      const url = `./data/${auditName}/${pageType}-page.json${cacheBuster}`;
      const response = await fetch(url);

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      this.cache.set(cacheKey, data);
      return data;
    } catch (error) {
      console.warn(`Failed to fetch audit data for ${pageType}:`, error);
      return null;
    }
  }

  /**
   * Fetch map data for curated content
   * @returns Map data or null if not found
   */
  async fetchMapData(): Promise<MapData | null> {
    if (this.mapDataCache) {
      return this.mapDataCache;
    }

    try {
      const cacheBuster = `?v=${Date.now()}`;
      const url = `./examples/map.json${cacheBuster}`;
      const response = await fetch(url);

      if (!response.ok) {
        return null;
      }

      this.mapDataCache = await response.json();
      return this.mapDataCache;
    } catch (error) {
      console.warn('Failed to fetch map data:', error);
      return null;
    }
  }

  /**
   * Fetch all page data for an audit and determine which pages exist
   * @param auditName - Name of the audit
   * @returns Object with existence flags and data
   */
  async fetchAllPages(auditName: string): Promise<{
    existence: PageExistence;
    data: Record<PageType, AuditData | null>;
  }> {
    const pageTypes: PageType[] = ['product', 'collection', 'home', 'cart'];

    const results = await Promise.all(
      pageTypes.map(pageType => this.fetchAuditData(auditName, pageType))
    );

    const existence: PageExistence = {
      product: results[0] !== null,
      collection: results[1] !== null,
      home: results[2] !== null,
      cart: results[3] !== null
    };

    const data: Record<PageType, AuditData | null> = {
      product: results[0],
      collection: results[1],
      home: results[2],
      cart: results[3]
    };

    return { existence, data };
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    this.cache.clear();
    this.mapDataCache = null;
  }
}
